/**
 * Get Kiosk Configuration Use Case
 * Loads kiosk UI customization from operator settings
 */

import { KioskConfigRepository } from '../data/kiosk-config-repository';
import { KioskUICustomization } from '../types/kiosk-types';
import { defaultKioskConfig, getDefaultKioskConfig } from '../constants/kiosk-defaults';
import { mergeWithDefaults } from '../../core/utils/config-serializer';

/**
 * Request for getting kiosk configuration
 */
export interface GetKioskConfigRequest {
  /** Operator ID to load config for */
  operatorId: number;

  /** Whether to use defaults if no config exists */
  useDefaults?: boolean;

  /** Preferred form factor for defaults */
  preferredFormFactor?: '4inch' | '5inch';
}

/**
 * Result of getting kiosk configuration
 */
export interface GetKioskConfigResult {
  /** Success status */
  success: boolean;

  /** Loaded configuration (or defaults) */
  config: KioskUICustomization;

  /** Whether this is a default config (not saved yet) */
  isDefault: boolean;

  /** Error message if failed */
  error?: string;
}

/**
 * Use case for loading kiosk UI customization
 *
 * Flow:
 * 1. Fetch operator data from API
 * 2. Extract uiCustomization from settings
 * 3. If not found, return defaults
 * 4. Merge with defaults to fill missing fields
 */
export class GetKioskConfigUseCase {
  constructor(private readonly repository: KioskConfigRepository) {}

  async execute(request: GetKioskConfigRequest): Promise<GetKioskConfigResult> {
    try {
      // Fetch operator settings
      const response = await this.repository.getOperatorSettings(request.operatorId);

      // Check if UI customization exists
      if (!response.uiCustomization) {
        // No config found, return defaults if requested
        if (request.useDefaults !== false) {
          const formFactor = request.preferredFormFactor || '4inch';
          const defaults = getDefaultKioskConfig(formFactor);

          return {
            success: true,
            config: {
              ...defaults,
              metadata: {
                ...defaults.metadata,
                operatorId: request.operatorId,
              },
            },
            isDefault: true,
          };
        }

        return {
          success: false,
          config: defaultKioskConfig,
          isDefault: true,
          error: 'No UI customization found for this operator',
        };
      }

      // Merge loaded config with defaults to ensure all fields are present
      const formFactor = response.uiCustomization.formFactor || '4inch';
      const defaults = getDefaultKioskConfig(formFactor);
      const mergedConfig = mergeWithDefaults(defaults, response.uiCustomization);

      return {
        success: true,
        config: mergedConfig,
        isDefault: false,
      };
    } catch (error) {
      console.error('Failed to load kiosk configuration:', error);

      // Return defaults on error if requested
      if (request.useDefaults !== false) {
        const formFactor = request.preferredFormFactor || '4inch';
        const defaults = getDefaultKioskConfig(formFactor);

        return {
          success: false,
          config: defaults,
          isDefault: true,
          error: error instanceof Error ? error.message : 'Failed to load configuration',
        };
      }

      throw error;
    }
  }
}
